/**
 * @Page RST02
 * @Description [RST02] 실사재고 > 기준정보 > 상주원 등록
 *
 * [좌측] 상주원 목록 조회/등록/삭제 (WRTRESI 상주원 마스터)
 * [우측] 선택한 상주원의 품목그룹코드 배정 목록 (WRTRESI 배정 데이터)
 *        - 신규 버튼: ResiGrp 팝업 → /rst01/waregroup/search 체크박스 선택 → INSERT
 *        - 삭제 버튼: 선택 행 → /rst02/resi/grpdelete
 **/
import React, { useState, useEffect, useRef, useCallback } from "react";
import Ribbon from '@/components/organisms/Ribbon';
import GridRibbon from "@/components/organisms/GridRibbon";
import ContentXearch from '@/components/organisms/ContentXearch';
import Content from '@/components/organisms/Content';
import Grid from '@/components/grid/agGrid';
import CustomAlert from "@/components/molecules/CustomAlert";
import AlertDialog from '@/components/material-UI/AlertDialog';
import Popup from '@/pages/Popup/Popup';
import * as Common from '@/utils/Common';
import CommonUtil from '@/utils/CommonUtil';


const SERVER_URL = process.env.REACT_APP_BACKEND_SERVER;
const Utils = new CommonUtil();

const useYnList = [
    { comCode: "*", text: "전체" },
    { comCode: "Y", text: "사용" },
    { comCode: "N", text: "미사용" },
];

function RST02() {
    const token = Common.getToken();
    const userId = Common.getToken("id");
    const tVnt1 = Common.getToken("vand");
    const tComp = Common.getToken("comp");

    const grdRST02List = useRef();  // 좌측: 상주원 마스터
    const grdRST02Detail = useRef();  // 우측: 품목그룹 배정 목록

    // ─── 공통 상태 ────────────────────────────────────────────────────────
    const [message, setMessage] = React.useState({ openAlert: false, severity: "success", msg: "" });
    const { openAlert, severity, msg } = message;

    const [dlgMessage, setDlgMessage] = React.useState({ openDlg: false, dlgTrans: "", dlgMsg: "" });
    const { openDlg, dlgTrans, dlgMsg } = dlgMessage;

    // ─── 검색 조건 ────────────────────────────────────────────────────────
    const ckVnt1 = Common.getCookie("ck_vnt1" + userId) || tVnt1;

    const [idata, setData] = useState({
        kVnt1: ckVnt1,
        kComp: tComp,
        kRecd: "",
        kUseyn: "*",
        vnt1List: [],
    });

    // ─── 레이아웃 ──────────────────────────────────────────────────────────
    const [panded, setPanded] = React.useState(true);
    const [style, setStyle] = useState({ height: "calc(100vh - 65px - 35px - 50px - 21px - 56px - 8px)" });

    // ─── 좌측 그리드 API ──────────────────────────────────────────────────
    let [searchApi, setSearchApi] = useState(false);
    let [insertApi, setInsertApi] = useState(false);
    let [deleteApi, setDeleteApi] = useState(false);

    const onClearApi = useCallback(() => {
        setSearchApi(false);
        setInsertApi(false);
        setDeleteApi(false);
    }, []);

    // ─── 우측 그리드 API ──────────────────────────────────────────────────
    let [searchApi2, setSearchApi2] = useState(false);
    let [deleteApi2, setDeleteApi2] = useState(false);
    let [clearGrid2, setClearGrid2] = useState(false);

    const onClearApi2 = useCallback(() => {
        setSearchApi2(false);
        setDeleteApi2(false);
    }, []);

    // ─── 현재 선택된 상주원 ────────────────────────────────────────────────
    const [currentRecd, setCurrentRecd] = useState("");
    const [currentRenm, setCurrentRenm] = useState("");

    // ─── ResiGrp 팝업 ─────────────────────────────────────────────────────
    const [resiGrpOpen, setResiGrpOpen] = useState(false);

    // ─── 마운트 ────────────────────────────────────────────────────────────
    useEffect(() => {
        onSearchCommon(`{"pgmid":"VNT1"}`, "vnt1List");
        onSearchLeft();
    }, []);

    // ─── 공통코드 조회 ─────────────────────────────────────────────────────
    const onSearchCommon = (para, name) => {
        const fetchData = async () => {
            const param = encodeURI(para);
            const query = SERVER_URL + "/comcd/search?query=" + param;
            await fetch(query, { headers: { Authorization: token } })
                .then(r => r.json())
                .then(data => {
                    if (data.success) setData(prev => ({ ...prev, [name]: data.data }));
                });
        };
        fetchData();
    };

    // ─── onChange ──────────────────────────────────────────────────────────
    const onChange = (e) => {
        const { name, value } = e.target;
        setData(prev => ({ ...prev, [name]: value }));
    };

    // ─── 좌측 조회 ─────────────────────────────────────────────────────────
    const onSearchLeft = () => {
        const para = `{"kVnt1":"${idata.kVnt1}","recd":"${idata.kRecd}","useyn":"${idata.kUseyn}"}`;
        setSearchApi(SERVER_URL + "/rst02/resi/search?query=" + encodeURI(para));
    };

    const onSearch = () => {
        Common.setCookie("ck_vnt1" + userId, idata.kVnt1, 1);
        onSearchLeft();
        // 우측 초기화
        setCurrentRecd("");
        setCurrentRenm("");
        setClearGrid2(true);
    };

    // ─── 우측 조회 (선택 상주원 기준) ─────────────────────────────────────
    const onSearchRight = (recd) => {   
        const para = `{"kVnt1":"${idata.kVnt1}","recd":"${recd}", "vand":"${idata.kVnt1}"}`;
        setSearchApi2(SERVER_URL + "/rst02/resi/grpsearch?query=" + encodeURI(para));
        // setSearchApi2(SERVER_URL + "/rst02/resi/grpsearch?query=" + encodeURI(para));
    };

    // ─── 좌측 신규 Row 추가 ────────────────────────────────────────────────
    const onAddRow = () => {
        grdRST02List.current.addRow(
            { vand: idata.kVnt1, recd: "", renm: "", reph: "", useyn: "Y", gocd: "ADD" },
            0
        );
    };

    // ─── 좌측 입력 유효성 검사 ─────────────────────────────────────────────
    const inputCheck = (gubn) => {
        //수정일경우에는 상주원 코드는 수정할 수 없다 구분자 추가해야한다.
        //inputcheck에서 수정항목 체크 해야한다.
        let gubntrue = (gubn === "I" ? true : false); // 입력에 해당하는 예외처리 사항

        const selected = grdRST02List.current?.getSelectedNodes() || [];
        if (selected.length === 0) {
            setMessage({ openAlert: true, severity: "info", msg: "선택된 데이터가 없습니다." });
            return false;
        }
        for (const node of selected) {
            const d = node.data;
            if (!d.recd && gubntrue) { // 입력조건
                setMessage({ openAlert: true, severity: "info", msg: "상주원코드를 입력하십시오." });
                grdRST02List.current.SetFocus(node.rowIndex, "recd");
                return false;
            }
            if (d.recd.length !== 7 && gubntrue) { // 위 부분 추가 ✔️ + 20260701
                setMessage({ openAlert : true, 
                             severity : "info",
                             msg : "사용자ID는 7자리로 입력하세요."});
                // inputFlag = false                             
                grdRST02List.current.SetFocus(node.rowIndex, "recd");                             
                return ;
            }
            if (d.recd !== null || d.recd !== "" && gubntrue) { // 위 부분 추가 ✔️ + 20260701
                    if (d.vand  >  " ")   
                    {
                        if (d.recd.substring(0, 4).toUpperCase() !== d.vand) {
                            setMessage({ openAlert : true, 
                                         severity : "info",
                                         msg : "업체코드를 포함한 상주원코드를 입력하여 주십시오."});
                            // inputFlag = false    
                            grdRST02List.current.SetFocus(node.rowIndex, "recd");                             
                            return;
                        }
                    }
                }
            if (!d.renm) {
                setMessage({ openAlert: true, severity: "info", msg: "상주원명을 입력하십시오." });
                grdRST02List.current.SetFocus(node.rowIndex, "renm");
                return false;
            }
            if (!d.useyn) {
                setMessage({ openAlert: true, severity: "info", msg: "사용여부를 선택하십시오." });
                grdRST02List.current.SetFocus(node.rowIndex, "useyn");
                return false;
            }
        }
        return true;
    };

    // ─── 좌측 입력 ─────────────────────────────────────────────────────────
    const onInsert = () => {
        if (!inputCheck("I")) return;
        setInsertApi(SERVER_URL + "/rst02/resi/insert");
    };

    const onUpdate = () => {
        if (!inputCheck("U")) return;
        setInsertApi(SERVER_URL + "/rst02/resi/update");
    };

    // ─── 좌측 삭제 ─────────────────────────────────────────────────────────
    const onDelete = () => {
        const selected = grdRST02List.current?.getSelectedNodes() || [];
        if (selected.length === 0) {
            setMessage({ openAlert: true, severity: "info", msg: "선택한 데이터가 없습니다." });
            return;
        }
        setDlgMessage({ openDlg: true, dlgTrans: "D_LEFT", dlgMsg: "선택한 상주원을 삭제하시겠습니까?" });
    };

    // ─── 좌측 Row 클릭 → 우측 그리드 조회 ────────────────────────────────
    const onRowClicked = (e) => {
        console.log("좌측 데이터 클릭e")
        console.log(e)
        if (!e.data) return;
        const { recd, renm } = e.data;
        if (!recd) return;
        setCurrentRecd(recd);
        setCurrentRenm(renm || "");
        onSearchRight(recd);
    };

    // ─── 우측 신규: ResiGrp 팝업 열기 ─────────────────────────────────────
    const onAddRow2 = () => {
        if (!currentRecd) {
            setMessage({ openAlert: true, severity: "info", msg: "좌측에서 상주원을 먼저 선택하십시오." });
            return;
        }
        setResiGrpOpen(true);
    };

    // ─── 우측 삭제 ─────────────────────────────────────────────────────────
    const onDelete2 = () => {
        const selected = grdRST02Detail.current?.getSelectedNodes() || [];
        if (selected.length === 0) {
            setMessage({ openAlert: true, severity: "info", msg: "선택한 데이터가 없습니다." });
            return;
        }
        setDlgMessage({ openDlg: true, dlgTrans: "D_RIGHT", dlgMsg: "선택한 품목그룹 배정을 삭제하시겠습니까?" });
    };

    // ─── AlertDialog 처리 ─────────────────────────────────────────────────
    const AlertDialogResult = (e) => {
        setDlgMessage({ openDlg: false, dlgTrans: "", dlgMsg: "" });
        if (e.result === "N") return;
        if (e.Trans === "D_LEFT") {
            setDeleteApi(SERVER_URL + "/rst02/resi/delete");
        }
        if (e.Trans === "D_RIGHT") {
            setDeleteApi2(SERVER_URL + "/rst02/resi/grpdelete");
        }
    };

    // ─── ResiGrp 팝업 닫기 → 우측 재조회 ────────────────────────────────
    const onResiGrpClose = () => {
        setResiGrpOpen(false);
        if (currentRecd) onSearchRight(currentRecd);
    };

    // ─── 검색조건 ─────────────────────────────────────────────────────────
    const optionsX = {
        0: [
            // {
            //     formatter: "select", name: "kVnt1", labelName: "T1업체", type: "text",
            //     defaultValue: idata.kVnt1, codeMode: true, selectAll: "",
            //     codes: null, comms: idata.vnt1List, align: "left",
            // },
            {
                formatter: "input", name: "kRecd", labelName: "상주원코드", type: "text",
                defaultValue: idata.kRecd, readOnly: false, align: "left", labelType: "none",
            },
            {
                formatter: "select", name: "kUseyn", labelName: "사용여부", type: "text",
                defaultValue: idata.kUseyn, codeMode: false, selectAll: "",
                codes: useYnList, comms: null, align: "left",
            },
            { formatter: "blank", align: "right" },
            { formatter: "blank", align: "right" },
        ],
    };

    // ─── 레이아웃 토글 ────────────────────────────────────────────────────
    const onClick = () => {
        const next = !panded;
        setPanded(next);
        setStyle({
            height: next
                ? "calc(100vh - 65px - 35px - 50px - 21px - 56px - 8px)"
                : "calc(100vh - 65px - 35px - 50px - 21px - 8px)",
        });
    };

    // ─── 좌측 컬럼 정의 ───────────────────────────────────────────────────
    const columnDefs1 = [
        {
            headerName: "업체코드", field: "vand",
            sortable: true, filter: true, flex: 1, minWidth: 90,
            editable: false, cellClass: ["nocheckbox", "alignC"],
            checkboxSelection: true, headerCheckboxSelection: true,
        },
        {
            headerName: "상주원코드", field: "recd",
            sortable: true, filter: true, flex: 1, minWidth: 100,
            editable: true, headerClass: "editCellOnlySub", cellClass: ["nocheckbox", "alignC"],
            singleClickEdit: true,
        },
        {
            headerName: "상주원명", field: "renm",
            sortable: true, filter: true, flex: 2, minWidth: 130,
            editable: true, headerClass: "editCellOnlySub", cellClass: ["nocheckbox", "alignL"],
            singleClickEdit: true,
            cellEditorParams:{maxLength:5}
        },
        {
            headerName: "연락처", field: "reph",
            sortable: true, filter: true, flex: 1, minWidth: 120,
            editable: true, headerClass: "editCellOnlySub", cellClass: ["nocheckbox", "alignC"],
            singleClickEdit: true,
        },
        {
            headerName: "사용여부", field: "useyn",
            sortable: true, filter: true, flex: 1, minWidth: 80,
            editable: true, headerClass: "editCellOnlySub", cellClass: ["nocheckbox", "alignC"],
            singleClickEdit: true,
            cellEditor: "agSelectCellEditor",
            cellEditorParams: { values: ["Y", "N"] },
        },
    ];

    // ─── 우측 컬럼 정의 (WRTRESI 배정 목록) ─────────────────────────────
    const columnDefs2 = [
        {
            headerName: "창고코드", field: "wrcd",
            sortable: true, filter: true, flex: 1, minWidth: 90,
            editable: false, cellClass: ["nocheckbox", "alignC"],
            checkboxSelection: true, headerCheckboxSelection: true,
        },
          {
            headerName: "창고명", field: "wrnm",
            sortable: true, filter: true, flex: 1, minWidth: 90,
            editable: false, cellClass: ["nocheckbox", "alignC"],
            checkboxSelection: true, headerCheckboxSelection: true,
        },
        {
            headerName: "품목그룹코드", field: "grpt",
            sortable: true, filter: true, flex: 1, minWidth: 110,
            editable: false, cellClass: ["nocheckbox", "alignC"],
        },
        // {
        //     headerName: "그룹코드명", field: "grnm",
        //     sortable: true, filter: true, flex: 2, minWidth: 130,
        //     editable: false, cellClass: ["nocheckbox", "alignL"],
        // },
    ];

    // ─── Render ───────────────────────────────────────────────────────────
    return (
        <>
            {/* Dialog */}
            <AlertDialog open={openDlg} trans={dlgTrans} message={dlgMsg} onClose={AlertDialogResult} />

            {/* Alert */}
            <CustomAlert
                open={openAlert} severity={severity} duration={4000} message={msg}
                onClose={() => setMessage({ openAlert: false, severity: "", msg: "" })}
                anchorOrigin={{ vertical: "top", horizontal: "center" }}
            />

            {/* ResiGrp 팝업: 품목그룹코드 배정 */}
            <Popup
                type={"ResiGrp"}
                open={resiGrpOpen}
                wrcd={currentRecd}          // recd로 전달 (Popup에서 wrcd prop으로 ResiGrp.recd에 매핑)
                vnt1={idata.kVnt1}
                para={onResiGrpClose}       // onRefresh 콜백 (팝업 확인 후 우측 재조회)
                onClose={onResiGrpClose}
            />

            {/* Ribbon */}
            <Ribbon onSearch={onSearch} onSearchName={null}>
                {"실사재고 > 기준정보 > "}
                <b style={{ color: "#cd5c5c", fontFamily: "PretendardMedium, NanumGothic, sans-serif" }}>상주원등록</b>
            </Ribbon>

            {/* 검색조건 */}
            <ContentXearch optionsX={optionsX} onChange={onChange} onClick={onClick} />

            {/* Content */}
            <Content style={style}>
                <div className="grid_type02">

                    {/* ── 좌측: 상주원 마스터 ── */}
                    <div className="grid_wrap02" style={{ width: "50%", height: "100%" }}>
                        <div className="grid_area">
                            <GridRibbon title={"상주원 등록"}
                                onSearch={onSearch} onSearchName={"조회"}
                                onAddRow={onAddRow} onAddRowName={"신규"}
                                onInsert={onInsert} onInsertName={"입력"}
                                onUpdate={onUpdate} onIpdateName={"수정"}
                                onDelete={onDelete} onDeleteName={"삭제"}
                            />
                            <div className="grid_cont_box" style={{ height: "calc(100%)" }}>
                                <Grid
                                    ref={grdRST02List}
                                    gridId={"RST021"}
                                    columnDefs={columnDefs1}
                                    pagination={false}
                                    paginationCsv={true}
                                    paginationPageSize={"1000"}
                                    rowMultiSelectWithClick={true}
                                    suppressRowClickSelection={false}
                                    rowSelection={"multiple"}
                                    enableRangeSelection={false}
                                    enableCellTextSelection={true}
                                    isFirst={true}
                                    displayNo={true}
                                    searchApi={searchApi}
                                    insertApi={insertApi}
                                    deleteApi={deleteApi}
                                    deleteRefresh={onSearchLeft}
                                    onClearApi={onClearApi}
                                    onRowClickEvent={onRowClicked}
                                    onCellValueChangedEvent={async () => { }}
                                    useMessage={false}
                                />
                            </div>
                        </div>
                    </div>

                    {/* ── 우측: 품목그룹 배정 목록 ── */}
                    <div className="grid_wrap03" style={{ height: "100%" }}>
                        <div className="grid_area">
                            <GridRibbon
                                title={currentRecd ? `품목그룹 배정 [${currentRecd}] ${currentRenm}` : "품목그룹 배정"}
                                onSearch={() => currentRecd && onSearchRight(currentRecd)} onSearchName={"조회"}
                                onAddRow={onAddRow2} onAddRowName={"등록/삭제"}
                                // onDelete={onDelete2} onDeleteName={"삭제"}
                            />
                            <div className="grid_cont_box" style={{ height: "calc(100%)" }}>
                                <Grid
                                    ref={grdRST02Detail}
                                    gridId={"RST022"}
                                    columnDefs={columnDefs2}
                                    pagination={false}
                                    paginationCsv={true}
                                    paginationPageSize={"1000"}
                                    rowMultiSelectWithClick={true}
                                    suppressRowClickSelection={false}
                                    rowSelection={"multiple"}
                                    enableRangeSelection={false}
                                    enableCellTextSelection={true}
                                    isFirst={true}
                                    displayNo={true}
                                    searchApi={searchApi2}
                                    deleteApi={deleteApi2}
                                    deleteRefresh={() => currentRecd && onSearchRight(currentRecd)}
                                    onClearApi={onClearApi2}
                                    onCellValueChangedEvent={async () => { }}
                                    useMessage={false}
                                    clearGrid={clearGrid2}
                                    onClearGridFlag={setClearGrid2}
                                />
                            </div>
                        </div>
                    </div>

                </div>
            </Content>
        </>
    );
}

export default RST02;
